import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from './Icons';
const photos = [{
  src: 'https://cdn.magicpatterns.com/uploads/oMBZmDCR2eWBRcVMnvdzwT/coast_8.png',
  alt: 'Palm tree trimming by climbing arborist',
  caption: 'Palm Trimming'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/hJ4GcCRq2f3Lmcn9iKwG2K/coast7.png',
  alt: 'Bucket truck and climber working on tall palms',
  caption: 'Bucket Truck Service'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/8cN2MdZozvWiNp9nM1rHmW/coast6.png',
  alt: 'Tree removal crew working on a residential property',
  caption: 'Tree Removal'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/7iHqu6a4ma8nWgxLBmMMJW/coast4.png',
  alt: 'Stump grinding in progress',
  caption: 'Stump Grinding'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/pyC2xK4qTZNiXt3PyvZCJD/coast3.png',
  alt: 'Crew loading cut branches into truck',
  caption: 'Cleanup & Hauling'
}];
export const GallerySection = () => {
  const [selectedPhoto, setSelectedPhoto] = useState<number | null>(null);
  return <section id="gallery" className="py-24 bg-white" data-id="element-116">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-117">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-118">
          <h2 className="text-3xl md:text-5xl font-bold text-charcoal mb-6" data-id="element-119">
            Our Work in Action
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-120">
            Real jobs. Real results. See what South Coast Tree Care can do for
            your property.
          </p>
        </motion.div>

        {/* Photo Grid - Masonry style */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-121">
          {/* First photo - tall, spans 2 rows */}
          <motion.button initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} onClick={() => setSelectedPhoto(0)} className="relative row-span-2 rounded-2xl overflow-hidden group cursor-pointer focus:outline-none focus:ring-4 focus:ring-accent/30" data-id="element-122">
            <img src={photos[0].src} alt={photos[0].alt} className="w-full h-full object-cover min-h-[300px] md:min-h-[500px] transition-transform duration-500 group-hover:scale-105" data-id="element-123" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-124" />
            <span className="absolute bottom-4 left-4 text-white font-bold text-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" data-id="element-125">
              {photos[0].caption}
            </span>
          </motion.button>

          {/* Photos 2-5 in a 2x2 grid */}
          {photos.slice(1).map((photo, index) => <motion.button key={index} initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: (index + 1) * 0.1
        }} onClick={() => setSelectedPhoto(index + 1)} className="relative rounded-2xl overflow-hidden group cursor-pointer aspect-square focus:outline-none focus:ring-4 focus:ring-accent/30" data-id="element-126">
              <img src={photo.src} alt={photo.alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-127" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-128" />
              <span className="absolute bottom-4 left-4 text-white font-bold text-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" data-id="element-129">
                {photo.caption}
              </span>
            </motion.button>)}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-130">
        {selectedPhoto !== null && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setSelectedPhoto(null)} data-id="element-131">
            <button onClick={() => setSelectedPhoto(null)} className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors z-50" aria-label="Close lightbox" data-id="element-132">
              <XIcon className="w-8 h-8" data-id="element-133" />
            </button>

            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25
        }} className="relative max-w-5xl max-h-[85vh] w-full" onClick={e => e.stopPropagation()} data-id="element-134">
              <img src={photos[selectedPhoto].src} alt={photos[selectedPhoto].alt} className="w-full h-full object-contain rounded-xl" data-id="element-135" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-xl" data-id="element-136">
                <p className="text-white font-bold text-xl" data-id="element-137">
                  {photos[selectedPhoto].caption}
                </p>
                <p className="text-white/70 text-sm mt-1" data-id="element-138">
                  {photos[selectedPhoto].alt}
                </p>
              </div>
            </motion.div>

            {/* Navigation arrows */}
            <button onClick={e => {
          e.stopPropagation();
          setSelectedPhoto(selectedPhoto > 0 ? selectedPhoto - 1 : photos.length - 1);
        }} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors backdrop-blur-sm" aria-label="Previous photo" data-id="element-139">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6" data-id="element-140">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" data-id="element-141" />
              </svg>
            </button>
            <button onClick={e => {
          e.stopPropagation();
          setSelectedPhoto(selectedPhoto < photos.length - 1 ? selectedPhoto + 1 : 0);
        }} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors backdrop-blur-sm" aria-label="Next photo" data-id="element-142">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6" data-id="element-143">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" data-id="element-144" />
              </svg>
            </button>
          </motion.div>}
      </AnimatePresence>
    </section>;
};